import { useState, type ReactNode } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { format, parseISO } from 'date-fns'
import { Plus, Inbox } from 'lucide-react'
import { useParams } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { authApi } from '@/api/auth'
import SectionCard from '@/components/dashboard/SectionCard'
import { EmptyState } from '@/components/dashboard/BarListWidget'
import { listMyRequests, cancelRequest, type LeaveRequest, type LeaveType } from '@/api/leaves'
import { listMyApprovals, type TimedRequest } from '@/api/attendanceApprovals'
import {
  myRegularizations, myOvertime, cancelRegularization, cancelOvertime,
  type Regularization, type Overtime,
} from '@/api/attendanceRequests'
import { listMyEmployeeRequests, cancelEmployeeRequest, type EmployeeRequest, type RequestCategory } from '@/api/requests'
import { ApplyModal, RequestRow } from '@/pages/Leaves'
import NewRequestModal from '@/components/requests/NewRequestModal'
import { CATEGORY_META, STATUS_CLS, money, typeMeta } from '@/components/requests/requestMeta'

const TIMED_LABEL: Record<TimedRequest['kind'], string> = {
  checkin: 'Check-in at a time',
  checkout: 'Check-out at a time',
  checkout_correction: 'Auto check-out correction',
  late_checkout: 'Late check-out',
}

type Category = 'all' | RequestCategory
type StatusFilter = 'all' | 'pending' | 'approved' | 'rejected'
interface Row { key: string; category: RequestCategory; status: string; created: string; node: ReactNode }

const fmtDay = (d: string) => format(parseISO(d), 'EEE, d MMM yyyy')
const fmtTime = (iso?: string | null) => (iso ? format(new Date(iso), 'h:mm a') : '—')

/** Every request the signed-in employee has raised — attendance, leave, finance and
 *  other — with a "New Request" button for any kind. */
export default function Requests() {
  const qc = useQueryClient()
  const { user } = useAuthStore()
  const { orgSlug } = useParams<{ orgSlug: string }>()
  // Respect modules switched off for this org (same source as the sidebar).
  const { data: features } = useQuery({
    queryKey: ['features', orgSlug], queryFn: authApi.getFeatures, staleTime: 60_000, enabled: !!user,
  })
  const ready = !!user?.id && features !== undefined
  const leavesOn = !features || features.includes('leaves')
  const attendanceOn = !features || features.includes('attendance')

  const [category, setCategory] = useState<Category>('all')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [showNew, setShowNew] = useState(false)
  const [leaveModal, setLeaveModal] = useState<{ type?: LeaveType; reversalOf?: LeaveRequest } | null>(null)

  const leave = useQuery({ queryKey: ['leaves', 'me', user?.id], queryFn: () => listMyRequests(), enabled: ready && leavesOn })
  const timed = useQuery({ queryKey: ['attendance-approvals', 'mine', user?.id], queryFn: listMyApprovals, enabled: ready && attendanceOn })
  const regs = useQuery({ queryKey: ['my-requests', 'reg', user?.id], queryFn: myRegularizations, enabled: ready && attendanceOn })
  const ots = useQuery({ queryKey: ['my-requests', 'ot', user?.id], queryFn: myOvertime, enabled: ready && attendanceOn })
  const general = useQuery({ queryKey: ['my-requests', 'general', user?.id], queryFn: listMyEmployeeRequests, enabled: !!user?.id })

  const [cancelError, setCancelError] = useState('')
  const cancelMut = useMutation({
    mutationFn: async ({ kind, id }: { kind: 'leave' | 'reg' | 'ot' | 'general'; id: string }): Promise<unknown> =>
      kind === 'leave' ? cancelRequest(id)
        : kind === 'reg' ? cancelRegularization(id)
        : kind === 'ot' ? cancelOvertime(id)
        : cancelEmployeeRequest(id),
    onSuccess: () => {
      setCancelError('')
      qc.invalidateQueries({ queryKey: ['leaves'] })
      qc.invalidateQueries({ queryKey: ['my-requests'] })
    },
    onError: (e: any) => setCancelError(e?.response?.data?.detail || 'Could not cancel the request'),
  })
  const cancel = (kind: 'leave' | 'reg' | 'ot' | 'general', id: string) => {
    if (window.confirm('Cancel this request?')) cancelMut.mutate({ kind, id })
  }

  const rows: Row[] = [
    ...(leave.data || []).map((r) => ({
      key: `l-${r.id}`, category: 'leave' as const, status: r.status, created: r.created_at,
      node: <RequestRow req={r} onCancel={(id) => cancel('leave', id)} onReverse={(req) => setLeaveModal({ reversalOf: req })} />,
    })),
    ...(timed.data || []).map((r) => ({
      key: `t-${r.id}`, category: 'attendance' as const, status: r.status, created: r.created_at,
      node: <TimedRow req={r} />,
    })),
    ...(regs.data || []).map((r) => ({
      key: `r-${r.id}`, category: 'attendance' as const, status: r.status, created: r.created_at,
      node: <CorrectionRow req={r} onCancel={() => cancel('reg', r.id)} />,
    })),
    ...(ots.data || []).map((r) => ({
      key: `o-${r.id}`, category: 'attendance' as const, status: r.status, created: r.created_at,
      node: <OvertimeRow req={r} onCancel={() => cancel('ot', r.id)} />,
    })),
    ...(general.data || []).map((r) => ({
      key: `g-${r.id}`, category: r.category, status: r.status, created: r.created_at,
      node: <GeneralRow req={r} onCancel={() => cancel('general', r.id)} />,
    })),
  ].sort((a, b) => b.created.localeCompare(a.created))

  const categories: Category[] = ['all', ...(attendanceOn ? ['attendance' as const] : []), ...(leavesOn ? ['leave' as const] : []), 'finance', 'other']
  const inCategory = rows.filter((r) => category === 'all' || r.category === category)
  const shown = inCategory.filter((r) => statusFilter === 'all' || r.status === statusFilter)
  const loading = [leave, timed, regs, ots, general].some((q) => q.isLoading)

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><Inbox className="h-6 w-6 text-brand" /> Requests</h1>
          <p className="text-sm text-gray-500">Raise any request — attendance, leave, finance or anything else — and track who it's with.</p>
        </div>
        <button onClick={() => setShowNew(true)}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-brand hover:bg-brand-dark rounded-lg shadow-sm shrink-0">
          <Plus className="h-4 w-4" /> New Request
        </button>
      </div>

      <div className="flex flex-col lg:flex-row gap-4 items-start">
        {/* Category panel (horizontal chips on small screens) */}
        <nav className="w-full lg:w-56 shrink-0 bg-white rounded-xl border border-gray-100 shadow-sm p-2 flex lg:flex-col gap-1 overflow-x-auto no-scrollbar">
          {categories.map((c) => {
            const { label, icon: Icon } = CATEGORY_META[c]
            const list = rows.filter((r) => c === 'all' || r.category === c)
            const pending = list.filter((r) => r.status === 'pending').length
            const active = category === c
            return (
              <button key={c} onClick={() => setCategory(c)}
                className={`flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm whitespace-nowrap transition-colors ${active ? 'bg-brand/10 text-brand font-medium' : 'text-gray-600 hover:bg-gray-50'}`}>
                <Icon className="h-4 w-4" />
                <span className="flex-1 text-left">{label}</span>
                {pending > 0 && <span className="rounded-full bg-amber-100 text-amber-700 text-[10px] font-semibold px-1.5 py-0.5">{pending}</span>}
                <span className="text-xs text-gray-400">{list.length}</span>
              </button>
            )
          })}
        </nav>

        <SectionCard
          className="flex-1 min-w-0 w-full"
          title={CATEGORY_META[category].label}
          action={
            <div className="flex gap-1">
              {(['all', 'pending', 'approved', 'rejected'] as StatusFilter[]).map((s) => (
                <button key={s} onClick={() => setStatusFilter(s)}
                  className={`px-2.5 py-1 rounded-full text-xs capitalize ${statusFilter === s ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-100'}`}>
                  {s}
                </button>
              ))}
            </div>
          }
          bodyClassName="p-0"
        >
          {cancelError && <p className="px-4 pt-3 text-sm text-red-600">{cancelError}</p>}
          {loading && !rows.length ? <p className="p-6 text-sm text-gray-400">Loading…</p>
            : shown.length === 0 ? <EmptyState label={statusFilter === 'all' ? 'No requests here yet' : `No ${statusFilter} requests`} />
            : <div className="divide-y divide-gray-100">{shown.map((r) => <div key={r.key}>{r.node}</div>)}</div>}
        </SectionCard>
      </div>

      {showNew && (
        <NewRequestModal
          onClose={() => setShowNew(false)}
          onLeave={(type) => { setShowNew(false); setLeaveModal({ type: type === 'wfh' ? 'wfh' : undefined }) }}
        />
      )}
      {leaveModal && (
        <ApplyModal
          reversalOf={leaveModal.reversalOf}
          initialType={leaveModal.type}
          onClose={() => setLeaveModal(null)}
          onCreated={() => qc.invalidateQueries({ queryKey: ['leaves'] })}
        />
      )}
    </div>
  )
}

/* ─── Rows ──────────────────────────────────────────────────────────────────── */

function Shell({ kind, title, status, created, children, onCancel, badge }: {
  kind: string; title: string; status: string; created: string; children: ReactNode
  onCancel?: () => void; badge?: ReactNode
}) {
  const { icon: Icon, tint } = typeMeta(kind)
  return (
    <div className="flex items-start gap-4 p-4 hover:bg-gray-50/60 transition-colors">
      <div className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${tint}`}><Icon className="h-5 w-5" /></div>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-gray-900">{title}</span>
          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border capitalize ${STATUS_CLS[status] || STATUS_CLS.cancelled}`}>{status}</span>
          {badge}
        </div>
        {children}
      </div>
      <div className="flex flex-col items-end gap-2 shrink-0">
        <span className="text-xs text-gray-400">{format(new Date(created), 'd MMM')}</span>
        {onCancel && status === 'pending' && (
          <button onClick={onCancel} className="text-xs font-medium text-red-600 hover:underline">Cancel</button>
        )}
      </div>
    </div>
  )
}

function Reviewed({ status, name, note }: { status: string; name?: string | null; note?: string | null }) {
  if (status === 'pending' || status === 'cancelled' || !name) return null
  return (
    <p className={`text-xs mt-1 ${status === 'approved' ? 'text-green-700' : 'text-red-600'}`}>
      {status === 'approved' ? 'Approved' : 'Rejected'} by <strong>{name}</strong>{note ? ` — ${note}` : ''}
    </p>
  )
}

function TimedRow({ req }: { req: TimedRequest }) {
  return (
    <Shell kind="timed" title={TIMED_LABEL[req.kind] || req.kind} status={req.status} created={req.created_at}
      badge={req.wfh && <span className="rounded-full bg-teal-50 px-2 py-0.5 text-[10px] font-medium text-teal-700">WFH</span>}>
      <p className="text-sm text-gray-600 mt-0.5">{fmtDay(req.date)} · {fmtTime(req.requested_time)}</p>
      {req.reason && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{req.reason}</p>}
      <Reviewed status={req.status} name={req.reviewer_name} note={req.review_note} />
      {req.status === 'pending' && <p className="text-xs text-amber-700 mt-1">Waiting for an admin or manager</p>}
    </Shell>
  )
}

function CorrectionRow({ req, onCancel }: { req: Regularization; onCancel: () => void }) {
  return (
    <Shell kind="attendance_correction" title="Check-in / Check-out correction" status={req.status} created={req.created_at} onCancel={onCancel}>
      <p className="text-sm text-gray-600 mt-0.5">
        {fmtDay(req.date)} · In {fmtTime(req.requested_checkin)} · Out {fmtTime(req.requested_checkout)}
      </p>
      <p className="text-xs text-gray-500 mt-1 line-clamp-2">{req.reason}</p>
      <Reviewed status={req.status} name={req.reviewer_name} note={req.review_note} />
      {req.status === 'pending' && <p className="text-xs text-amber-700 mt-1">Waiting for your manager</p>}
    </Shell>
  )
}

function OvertimeRow({ req, onCancel }: { req: Overtime; onCancel: () => void }) {
  return (
    <Shell kind="overtime" title={`Overtime · ${Number(req.hours)}h`} status={req.status} created={req.created_at} onCancel={onCancel}>
      <p className="text-sm text-gray-600 mt-0.5">{fmtDay(req.date)}</p>
      <p className="text-xs text-gray-500 mt-1 line-clamp-2">{req.reason}</p>
      <Reviewed status={req.status} name={req.reviewer_name} note={req.review_note} />
      {req.status === 'pending' && <p className="text-xs text-amber-700 mt-1">Waiting for your manager</p>}
    </Shell>
  )
}

function GeneralRow({ req, onCancel }: { req: EmployeeRequest; onCancel: () => void }) {
  const dates = req.start_date
    ? (req.end_date && req.end_date !== req.start_date ? `${fmtDay(req.start_date)} → ${fmtDay(req.end_date)}` : fmtDay(req.start_date))
    : null
  const facts = [
    req.title,
    req.amount != null ? money(req.amount) : null,
    req.kind === 'loan' && req.installments ? `${req.installments} instalments` : null,
    dates,
  ].filter(Boolean)
  return (
    <Shell kind={req.kind} title={req.kind_label} status={req.status} created={req.created_at} onCancel={onCancel}>
      {facts.length > 0 && <p className="text-sm text-gray-600 mt-0.5">{facts.join(' · ')}</p>}
      <p className="text-xs text-gray-500 mt-1 line-clamp-2">{req.details}</p>
      {req.attachment_url && (
        <a href={req.attachment_url} target="_blank" rel="noopener noreferrer" className="text-xs text-brand hover:underline mt-1 inline-block">View receipt</a>
      )}
      {req.manager_reviewer_name && (req.status === 'pending' || req.reviewer_name !== req.manager_reviewer_name) && (
        <p className="text-xs text-green-700 mt-1">Manager approval: <strong>{req.manager_reviewer_name}</strong>{req.manager_note ? ` — ${req.manager_note}` : ''}</p>
      )}
      <Reviewed status={req.status} name={req.reviewer_name} note={req.review_note} />
      {req.status === 'pending' && req.waiting_on && <p className="text-xs text-amber-700 mt-1">Waiting for {req.waiting_on}</p>}
      {req.loan && (
        <p className="text-xs text-gray-600 mt-1">
          {money(req.loan.instalment)} / month from {req.deduction_start ? format(parseISO(req.deduction_start), 'MMM yyyy') : '—'} ·
          {' '}repaid {money(req.loan.repaid)} · <strong>{money(req.loan.outstanding)} left</strong>
        </p>
      )}
    </Shell>
  )
}
